/**
 * Cliente tipado a `/api/admin/reports/*` — la sección analítica del BO.
 * Mismo patrón que `costs.ts`: `URLSearchParams` + `get<T>`.
 */
import { get } from './_core.js';

export type ReportDimension =
  | 'day'
  | 'week'
  | 'month'
  | 'user'
  | 'module'
  | 'provider'
  | 'model'
  | 'kind';

export const REPORT_DIMENSIONS: ReportDimension[] = [
  'day',
  'week',
  'month',
  'user',
  'module',
  'provider',
  'model',
  'kind',
];

/** Filtros combinables compartidos por todos los reportes. */
export interface ReportFilterParams {
  from?: string;
  to?: string;
  userId?: string;
  moduleId?: string;
  provider?: string;
  model?: string;
  kind?: string;
  /** Costo en nano-USD (string bigint). */
  minCostNano?: string;
  maxCostNano?: string;
  includeSynthetic?: boolean;
}

export interface PivotRow {
  rowKey: string | null;
  colKey: string | null;
  costNano: string;
  callCount: number;
  errorCount: number;
}

export interface PivotResponse {
  rows: PivotRow[];
  rowDim: ReportDimension;
  colDim: ReportDimension | null;
}

function applyFilters(params: URLSearchParams, f: ReportFilterParams): void {
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (f.userId) params.set('user_id', f.userId);
  if (f.moduleId) params.set('module_id', f.moduleId);
  if (f.provider) params.set('provider', f.provider);
  if (f.model) params.set('model', f.model);
  if (f.kind) params.set('kind', f.kind);
  if (f.minCostNano) params.set('min_cost_nano', f.minCostNano);
  if (f.maxCostNano) params.set('max_cost_nano', f.maxCostNano);
  if (f.includeSynthetic) params.set('include_synthetic', 'true');
}

export async function fetchPivot(opts: {
  rowDim: ReportDimension;
  colDim?: ReportDimension | null;
  filters?: ReportFilterParams;
}): Promise<PivotResponse> {
  const params = new URLSearchParams();
  params.set('row_dim', opts.rowDim);
  if (opts.colDim) params.set('col_dim', opts.colDim);
  if (opts.filters) applyFilters(params, opts.filters);
  const res = await get<PivotResponse>(`/admin/reports/pivot?${params.toString()}`);
  return res.data;
}

export interface ModuleReportRow {
  module: string | null;
  costNano: string;
  callCount: number;
  sessionCount: number;
  activeUsers: number;
  costPerSessionNano: string;
  kindBreakdown: Array<{ kind: string; costNano: string }>;
}

export async function fetchByModule(filters?: ReportFilterParams): Promise<ModuleReportRow[]> {
  const params = new URLSearchParams();
  if (filters) applyFilters(params, filters);
  const qs = params.toString();
  const res = await get<ModuleReportRow[]>(`/admin/reports/by-module${qs ? `?${qs}` : ''}`);
  return res.data;
}

export interface PnlRow {
  bucket: string | null;
  revenueNano: string;
  costNano: string;
  marginNano: string;
  marginPct: number | null;
  callCount: number;
}

export interface PnlResponse {
  rows: PnlRow[];
  groupBy: ReportDimension;
}

export async function fetchPnl(opts: {
  groupBy: ReportDimension;
  filters?: ReportFilterParams;
}): Promise<PnlResponse> {
  const params = new URLSearchParams();
  params.set('group_by', opts.groupBy);
  if (opts.filters) applyFilters(params, opts.filters);
  const res = await get<PnlResponse>(`/admin/reports/pnl?${params.toString()}`);
  return res.data;
}

export interface ModelEfficiencyRow {
  provider: string;
  model: string;
  callCount: number;
  costNano: string;
  costPerCallNano: string;
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  cacheHitRatio: number | null;
  latencyP50Ms: number | null;
  latencyP95Ms: number | null;
  ttftP50Ms: number | null;
  retryRate: number;
}

export async function fetchModelEfficiency(
  filters?: ReportFilterParams,
): Promise<ModelEfficiencyRow[]> {
  const params = new URLSearchParams();
  if (filters) applyFilters(params, filters);
  const qs = params.toString();
  const res = await get<ModelEfficiencyRow[]>(`/admin/reports/model-efficiency${qs ? `?${qs}` : ''}`);
  return res.data;
}

export interface CostDetailRow {
  operation: string | null;
  kind: string;
  model: string | null;
  provider: string | null;
  callCount: number;
  costNano: string;
  avgCostNano: string;
}

export async function fetchCostDetail(filters?: ReportFilterParams): Promise<CostDetailRow[]> {
  const params = new URLSearchParams();
  if (filters) applyFilters(params, filters);
  const qs = params.toString();
  const res = await get<CostDetailRow[]>(`/admin/reports/cost-detail${qs ? `?${qs}` : ''}`);
  return res.data;
}

export interface TransactionRow {
  spanId: string;
  startedAt: string;
  operation: string | null;
  kind: string;
  model: string | null;
  provider: string | null;
  costNano: string;
  durationMs: number | null;
  isError: boolean;
  userId: string | null;
  userEmail: string | null;
  sessionId: string | null;
}

export interface TransactionsPage {
  items: TransactionRow[];
  nextCursor?: string;
}

export async function fetchTransactions(opts: {
  filters?: ReportFilterParams;
  operation?: string | null;
  cursor?: string | null;
  limit?: number;
}): Promise<TransactionsPage> {
  const params = new URLSearchParams();
  if (opts.filters) applyFilters(params, opts.filters);
  if (opts.operation) params.set('operation', opts.operation);
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.limit) params.set('limit', String(opts.limit));
  const res = await get<TransactionRow[]>(`/admin/reports/transactions?${params.toString()}`);
  return { items: res.data, ...(res.meta?.nextCursor ? { nextCursor: res.meta.nextCursor } : {}) };
}
